import { createTheme, lighten } from '@mui/material/styles';
import { scrollX, scrollY } from './scrollbars';

// Color tokens for the two modes. Light keeps the historical values
// (so nothing visible changes in light mode); dark is a deliberate
// inversion that preserves the brand palette.
const lightPalette = {
  mode: 'light',
  primary: { main: '#252525' },
  white: { main: '#fff' },
  secondary: { main: '#19857b' },
  error: { main: '#D8364F' },
  success: { main: '#20A766' },
  warning: { main: '#BE8900' },
  accent: { main: '#2075CE' },
  spacer: { main: '#EAEAEA' },
  cancelled: { main: '#686868', contrastText: '#252525' },
  divider: '#dbdbdb',
  text: {
    primary: '#252525',
    secondary: '#686868',
    inactive: '#ACACAC',
  },
  background: {
    paper: '#FFFFFF',
    default: '#F2F2F2',
  },
  brand: { saltPonds: '#FF6837' },
};

const darkPalette = {
  mode: 'dark',
  primary: { main: '#FFFFFF' },
  white: { main: '#fff' },
  secondary: { main: '#19857b' },
  error: { main: '#FF6678' },
  success: { main: '#3DD68C' },
  warning: { main: '#E5A93A' },
  accent: { main: '#5AA9F0' },
  spacer: { main: 'rgba(255, 255, 255, 0.08)' },
  cancelled: { main: 'rgba(255, 255, 255, 0.45)', contrastText: '#FFFFFF' },
  divider: 'rgba(255, 255, 255, 0.12)',
  text: {
    primary: '#FFFFFF',
    secondary: 'rgba(255, 255, 255, 0.65)',
    inactive: 'rgba(255, 255, 255, 0.4)',
  },
  background: {
    paper: '#141416',
    default: '#0a0a0c',
  },
  brand: { saltPonds: '#FF6837' },
};

// Build a theme bound to a specific mode. App.js calls this whenever
// the user's prefers-color-scheme media query flips.
export const buildTheme = (mode = 'light') => {
  const palette = mode === 'dark' ? darkPalette : lightPalette;

  return createTheme({
    palette,
    typography: {
      fontFamily:
        '"Haas-Grot-Text-App", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
      fontWeight: 500,
      fontSize: 16,
      lineHeight: '24px',
      h1: { fontSize: '22px', lineHeight: '32px', fontWeight: 600 },
      h2: { fontSize: '20px', lineHeight: '32px', fontWeight: 500 },
      h5: { fontSize: '20px', lineHeight: '28px', fontWeight: 700 },
      body1: { fontWeight: 500, fontSize: 16, lineHeight: '24px' },
      body2: { fontWeight: 500, fontSize: 14, lineHeight: '24px' },
      pageAvatar: { fontSize: '22px', lineHeight: '16px', fontWeight: 700 },
      subtitle1: { fontSize: '22px', lineHeight: '32px', fontWeight: 600 },
      small: { fontSize: '12px', lineHeight: '14px', fontWeight: 500 },
    },
    components: {
      MuiSvgIcon: {
        defaultProps: {},
      },
      MuiChip: {
        variants: [{ props: { variant: 'text' }, style: {} }],
        defaultProps: { variant: 'text' },
        styleOverrides: {
          root: ({ ownerState, theme }) => {
            const style = {
              padding: '4px 8px',
              borderRadius: 4,
              cursor: 'inherit',
            };
            if (ownerState.variant === 'text') {
              const paletteColor = theme.palette[ownerState.color];
              if (paletteColor) {
                style.backgroundColor =
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.08)'
                    : lighten(paletteColor.main, 0.8);
              } else {
                style.backgroundColor =
                  theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#eee';
              }
            }
            return style;
          },
          icon: ({ ownerState, theme }) => {
            const style = { marginLeft: 0, marginRight: 6 };
            if (ownerState.variant === 'text') {
              style.color = theme.palette[ownerState.color]
                ? theme.palette[ownerState.color].main
                : theme.palette.text.primary;
            }
            return style;
          },
          label: ({ ownerState, theme }) => {
            const style = { fontWeight: 500, paddingLeft: 0, paddingRight: 0 };
            if (ownerState.variant === 'text') {
              style.color = theme.palette[ownerState.color]
                ? theme.palette[ownerState.color].main
                : theme.palette.text.primary;
            }
            return style;
          },
          deleteIcon: () => ({ marginLeft: 5 }),
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { textTransform: 'none' },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 32 },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: ({ theme }) => ({
            textTransform: 'none',
            padding: 4,
            fontSize: 16,
            lineHeight: 1.5,
            fontWeight: 500,
            minHeight: 32,
            color: theme.palette.text.inactive,
            '&:not(:last-child)': { marginRight: 32 },
          }),
        },
      },
      MuiSwitch: {
        defaultProps: { disableRipple: true },
        styleOverrides: {
          root: { padding: 0, height: 16, width: 26 },
          switchBase: () => ({
            padding: 2,
            '&.Mui-checked': {
              transform: 'translateX(10px)',
              '+ .MuiSwitch-track': { opacity: 1 },
            },
          }),
          thumb: () => ({ height: 12, width: 12, backgroundColor: '#fff' }),
          input: { left: 0, width: '200%' },
          track: ({ theme }) => ({
            backgroundColor:
              theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.25)' : '#DBDBDB',
            opacity: 1,
            borderRadius: 20,
          }),
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: ({ theme }) => ({
            border: `1px solid ${theme.palette.divider}`,
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.background.paper,
          }),
          virtualScroller: { ...scrollX },
          columnSeparator: { display: 'none' },
          columnHeaders: ({ theme }) => ({
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
          }),
          columnHeader: { padding: '0 32px' },
          menuIcon: { display: 'none' },
          columnHeaderTitle: ({ theme }) => ({
            color: theme.palette.text.secondary,
            fontSize: '16px',
            lineHeight: '24px',
            fontWeight: 500,
          }),
          cell: ({ theme }) => ({
            padding: '0 32px',
            borderBottom: `1px solid ${theme.palette.divider}`,
          }),
          cellContent: ({ theme }) => ({
            color: theme.palette.text.primary,
            fontSize: '16px',
            lineHeight: '24px',
            fontWeight: 500,
          }),
          footerContainer: { border: 'none' },
        },
      },
      MuiPagination: {
        defaultProps: {
          color: 'spacer',
          shape: 'rounded',
          siblingCount: 3,
          boundaryCount: 1,
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: { padding: '12px 16px' },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: { padding: '0px 9px' },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: 0 },
        },
      },
      MuiInputLabel: {
        defaultProps: { shrink: true, disableAnimation: true },
      },
      MuiTextField: {
        defaultProps: { InputLabelProps: { shrink: true } },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          label: ({ theme }) => ({
            fontSize: 14,
            lineHeight: '32px',
            marginLeft: '8px',
            color: theme.palette.text.secondary,
          }),
        },
      },
      MuiAccordion: {
        defaultProps: { disableGutters: true, defaultExpanded: true },
        styleOverrides: {
          root: { '&:before': { opacity: 0 } },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: { padding: 0 },
          content: { margin: '24px 0 24px 0' },
        },
      },
      MuiAccordionDetails: {
        styleOverrides: {
          root: { padding: 0 },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: { ...scrollY },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: ({ theme }) => ({
            justifyContent: 'center',
            boxShadow:
              theme.palette.mode === 'dark'
                ? '0px 4px 40px rgba(0, 0, 0, 0.6)'
                : '0px 4px 40px rgba(0, 0, 0, 0.05)',
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
          }),
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: { paddingTop: '18px', paddingBottom: '18px' },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: ({ theme }) => ({
            borderRight: 'none',
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
          }),
        },
      },
      MuiAlert: {
        styleOverrides: {
          standard: ({ ownerState, theme }) => {
            const color =
              theme.palette[ownerState.color || ownerState.severity].main;
            return {
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.06)'
                  : lighten(color, 0.8),
              color: theme.palette.text.primary,
            };
          },
        },
      },
    },
  });
};

// Backwards-compat default export — light theme matches the historical
// `theme` import used in older code paths.
const theme = buildTheme('light');

export default theme;
